/* eslint-disable react/prop-types */
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExportOutlined,
  FilterOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { Eye, Pencil, File as FileIcon, FileText } from 'lucide-react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Space,
  Table,
  Tooltip,
  Typography,
  Select,
  notification,
  Tag,
  Row,
  Col,
  Drawer,
  Upload,
  Progress,
} from 'antd';
import { get, set } from 'lodash';
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Spinner, Button as CrcButton } from '@crc-evans/ui-components';

import ExcelJS from 'exceljs';
import moment from 'moment';
import history from '../../services/history';
import ProjectDetails from './ProjectDetails';
import TechnicalReportPage from './TechnicalReportPage';

import PDFViewer from './PDFViewer';
import axiosConfig from '../../redux/CRCETechnical/axiosConfig';
import { fetchProjectList, updateProjectTestList } from '../../redux/CRCETechnical/action';
import {
  getCategoryListFromDatabse,
  getExportDataListFromDatabse,
} from '../../redux/CRCETechnical/saga';
import { object } from 'prop-types';
import UpdateReport from './UpdateReport';
import CsvUpdatePreviewModal from './CsvUpdatePreviewModal';
import { parseCsvUpdateFile } from './CsvUpdateParser';
import logoCrc from '../../assets/logoCrc.png';
//ag grid enterprise
import 'ag-grid-enterprise';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { StatusBarModule } from '@ag-grid-enterprise/status-bar';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { CustomLoadingCellRenderer } from './Uitiles';
import { useHeatStyleColumnFacets, buildCriteriaWithColumnFacets } from '../technical/heatStyleColumnFacets';

// import axiosConfig from './axiosConfig';
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  MenuModule,
  SetFilterModule,
  StatusBarModule,
  RowGroupingModule,
]);

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_LICENSE);

// Length unit conversion: mm → display (same factors as CsvUpdateParser)
const INCH_TO_MM = 25.4;
const LENGTH_UNIT_LABELS = { mm: 'mm', inch: 'inch', cm: 'cm', m: 'm', ft: 'ft' };

/**
 * Convert a value in mm to the given display unit. Returns formatted string for display.
 * @param {number|string} valueMm - Value in mm (from DB)
 * @param {string} unit - One of 'mm' | 'inch' | 'cm' | 'm' | 'ft'
 * @returns {string} Formatted value with unit label, or original value / '-' for non-numeric
 */
const convertMmToDisplay = (valueMm, unit) => {
  if (valueMm === null || valueMm === undefined || valueMm === '') return '-';
  const num = typeof valueMm === 'number' ? valueMm : parseFloat(String(valueMm).trim());
  if (Number.isNaN(num)) return valueMm ?? '-';
  let converted;
  switch (unit) {
    case 'inch':
      converted = num / INCH_TO_MM;
      break;
    case 'cm':
      converted = num / 10;
      break;
    case 'm':
      converted = num / 1000;
      break;
    case 'ft':
      converted = num / 304.8;
      break;
    default:
      converted = num;
  }
  const label = LENGTH_UNIT_LABELS[unit] || unit;
  return `${Number(Math.round(converted * 1000) / 1000)} ${label}`;
};

/** Header filter text → mm string for GetReportList (DB stores mm; user may type display units). */
const modernLengthSearchToMm = (rawInput, displayUnit) => {
  const trimmed = String(rawInput || '').trim();
  if (!trimmed) return '';
  const lower = trimmed.toLowerCase();
  const hasExplicitUnit =
    /\b(mm|inch|inches|in\b|cm\b|ft\b|feet)\b/i.test(trimmed) ||
    (/\d\s+m\s*$/i.test(trimmed) && !lower.endsWith('mm')) ||
    /["""′']/.test(trimmed);
  if (hasExplicitUnit) {
    return parseLengthToMm(trimmed);
  }
  const normalized = trimmed.replace(',', '.');
  const bare = parseFloat(normalized);
  if (Number.isNaN(bare)) return '';
  let mm;
  switch (displayUnit) {
    case 'inch':
      mm = bare * INCH_TO_MM;
      break;
    case 'cm':
      mm = bare * 10;
      break;
    case 'm':
      mm = bare * 1000;
      break;
    case 'ft':
      mm = bare * 304.8;
      break;
    default:
      mm = bare;
  }
  return String(Math.round(mm * 1000) / 1000);
};

/** Fields GetReportList does not filter reliably — stripped from bulk fetch searchKey and applied in the browser. */
const MODERN_STRIP_FROM_BULK_SEARCH_KEY = [
  'ID',
  'JointReference',
  'WallThickness',
  'PipeDiameter',
  'Company',
  'Contractor',
  'Filename',
];

const stripSearchKeyForBulkFetch = (criteria) => {
  if (!criteria || typeof criteria !== 'object') return {};
  const out = { ...criteria };
  MODERN_STRIP_FROM_BULK_SEARCH_KEY.forEach((k) => {
    delete out[k];
  });
  return out;
};

/** Text columns that support Heat Profile–style funnel facets + header search (Test Repository modern table). */
const TECH_MODERN_FACET_COLUMNS = [
  'ID',
  'Material',
  'TestType',
  'Vendor',
  'ProjectName',
  'Timeline',
  'Temperature',
  'Company',
  'Contractor',
  'FileName',
  'ReportNumber',
  'JointReference',
  'WallThickness',
  'PipeDiameter',
];

const LENGTH_UNIT_OPTIONS = [
  { value: 'mm', label: 'mm' },
  { value: 'inch', label: 'inch' },
  { value: 'cm', label: 'cm' },
  { value: 'm', label: 'm', group: 'More' },
  { value: 'ft', label: 'ft', group: 'More' },
];

const LengthUnitFloatingFilter = React.forwardRef((props, ref) => {
  const { api, column, parentFilterInstance, displayUnit, onDisplayUnitChange, columnId } = props;
  const [filterText, setFilterText] = useState('');

  const syncFromParentModel = useCallback((parentModel) => {
    if (!parentModel || !parentModel.filterModels) {
      setFilterText('');
      return;
    }
    const textModel = parentModel.filterModels[0];
    setFilterText(textModel?.filter ?? '');
  }, []);

  React.useImperativeHandle(ref, () => ({
    onParentModelChanged(parentModel) {
      syncFromParentModel(parentModel);
    },
  }));

  useEffect(() => {
    if (parentFilterInstance && typeof parentFilterInstance.getModel === 'function') {
      const model = parentFilterInstance.getModel();
      syncFromParentModel(model);
    }
  }, [parentFilterInstance, syncFromParentModel]);

  const onFilterInputChange = (e) => {
    const value = e.target?.value ?? '';
    setFilterText(value);
    if (!api || !column) return;
    const colId = column.getColId();
    const currentModel = api.getFilterModel() || {};
    const existingColumnModel = currentModel[colId];
    const existingModels = existingColumnModel?.filterModels ?? [null, null];
    const textModel = value ? { filterType: 'text', filter: value, type: 'contains' } : null;
    const filterModels = [textModel, existingModels[1]];
    const newColumnModel = filterModels.some(Boolean)
      ? { filterType: 'multi', filterModels }
      : null;
    api.setFilterModel({ ...currentModel, [colId]: newColumnModel });
  };

  const onUnitChange = (newUnit) => {
    onDisplayUnitChange?.(newUnit);
    api?.refreshCells({ columns: [columnId], force: true });
  };

  return (
    <div className="ag-floating-filter-body" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '4px' }}>
      <div className="ag-input-wrapper" role="presentation" style={{ flex: 1, minWidth: 0 }}>
        <input
          className="ag-floating-filter-input"
          type="text"
          value={filterText}
          onChange={onFilterInputChange}
          aria-label="Search"
        />
      </div>
      <Select
        value={displayUnit}
        onChange={onUnitChange}
        style={{ minWidth: 52, flexShrink: 0 }}
        size="small"
        options={LENGTH_UNIT_OPTIONS.map(({ value, label }) => ({ value, label }))}
      />
    </div>
  );
});

LengthUnitFloatingFilter.displayName = 'LengthUnitFloatingFilter';

const { Option } = Select;
const { Title, Text } = Typography;

const TechnicalPageAgGrid = (props) => {
  const { projectList, isLoading, listCount, onProjectList, listTotalCount, embeddedInModernLayout = false, onUpdateReportt } = props;
  const data = [
    {
      key: '1',
      MaterialName: '3M Scotchkote 226N FBE_Borealis EP5 10/60M CMPP_Borealis EP5 10/60 PP Tape',
      BatchNumber: 'IN02 5E14_PL101HX01_NH1341E01',
      TestedDate: '2024-01-15',
      ReportedDate: '2024-02-20',
      // Add other properties as needed
    },
    {
      key: '1',
      MaterialName: '3M Scotchkote 226N ',
      BatchNumber: 'IN02 / 4L10 (804)',
      TestedDate: '2024-01-15',
      ReportedDate: '2024-02-20',
      // Add other properties as needed
    },
    // Add more objects as needed
  ];

  const [theme, setTheme] = useState('ag-theme-material-dark'); // Default theme
  const gridRef = useRef();

  const [showAddNewModal, setShowAddNewModal] = useState(false);
  const [addNewForm] = Form.useForm();
  const [showTestPage, setshowTestPage] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const [isEdit, setIsEdit] = useState(false);
  const [editRecord, seteditRecord] = useState('');
  const [current, setCurrent] = useState(1);
  const [currentPagesize, setCurrentPagesize] = useState(10);
  const [prevPageNumber, setPrevPageNumber] = useState(1);
  const [prevPagesize, setPrevPagesize] = useState(10);
  const [selectedProject, setSelectedProject] = useState('');
  //const defaultLayoutPluginInstance = defaultLayoutPlugin();
  const [resultModalVisible, setResultModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [categoryType, setCategoryType] = useState([]);
  const [materialType, setMaterialType] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState([]);
  const [temperatureType, setTemperatureType] = useState([]);
  const [fileNameType, setFileNameType] = useState([]);
  const [selectedTemperature, setSelectedTemperature] = useState([]);
  const [selectedFileName, setSelectedFileName] = useState([]);
  const [timelineType, setTimelineType] = useState([]);
  const [selectedTimeline, setSelectedTimeline] = useState([]);
  const [projectNameType, setProjectNameType] = useState([]);
  const [selectedProjectName, setSelectedProjectName] = useState([]);
  const [matrialSearchType, setMatrialSearchType] = useState('');
  const [testTypeSearchType, setTestTypeSearchType] = useState('');
  const [projectNameSearchType, setProjectNameSearchType] = useState('');
  const [timelineSearchType, setTimelineSearchType] = useState('');
  const [temperatureSearchType, setTemperatureSearchType] = useState('');
  const [fileNameSearchType, setFileNameSearchType] = useState('');
  const [searchType, setSearchType] = useState({});
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [searchFilterCriteria, setSearchFilterCriteria] = useState({});
  const [rowData, setRowData] = useState([]);
  const [csvPreviewVisible, setCsvPreviewVisible] = useState(false);
  const [csvPreviewData, setCsvPreviewData] = useState([]);
  const [csvUpdateLoading, setCsvUpdateLoading] = useState(false);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [updateProgress, setUpdateProgress] = useState({ current: 0, total: 0 });

  /* Modern layout (Test Repository page): column filters and pagination */
  const [columnFilters, setColumnFilters] = useState({
    ID: '',
    Material: '',
    TestType: '',
    Vendor: '',
    ProjectName: '',
    Timeline: '',
    Temperature: '',
    JointReference: '',
    WallThickness: '',
    PipeDiameter: '',
    Company: '',
    Contractor: '',
    FileName: '',
    ReportNumber: '',
  });
  const [filterTestedDate, setFilterTestedDate] = useState(null);
  const [filterReportedDate, setFilterReportedDate] = useState(null);
  const [modernPageNumber, setModernPageNumber] = useState(1);
  const [modernPageSize, setModernPageSize] = useState(10);
  const [modernSearchCriteria, setModernSearchCriteria] = useState({});
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  /** Non-empty when Test Repository ID header filter has text (backend searchKey does not apply ID reliably). */
  const modernIdFilterNeedle = embeddedInModernLayout
    ? String(columnFilters.ID || '').trim()
    : '';

  const MODERN_CLIENT_FILTER_COLUMNS = useMemo(
    () => ['JointReference', 'WallThickness', 'PipeDiameter', 'Company', 'Contractor', 'FileName'],
    [],
  );
  /** Bulk-load + client filtering when ID or any column the API does not honor is filtered. */
  const modernBulkFilterActive = useMemo(() => {
    if (!embeddedInModernLayout) return false;
    if (modernIdFilterNeedle) return true;
    return MODERN_CLIENT_FILTER_COLUMNS.some((k) => String(columnFilters[k] ?? '').trim() !== '');
  }, [embeddedInModernLayout, modernIdFilterNeedle, MODERN_CLIENT_FILTER_COLUMNS, columnFilters]);

  const handleColumnFilterChange = useCallback((key, value) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    if (embeddedInModernLayout) setModernPageNumber(1);
  }, [embeddedInModernLayout]);

  const fetchTechnicalFacetRows = useCallback(async ({ pageNumber, pageSize, searchKey }) => {
    let url = `/GetReportList?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (searchKey && typeof searchKey === 'object' && Object.keys(searchKey).length > 0) {
      url += `&searchKey=${encodeURIComponent(JSON.stringify(searchKey))}`;
    }
    const res = await axiosConfig.get(url);
    const data = get(res, ['data', 'data'], []);
    return Array.isArray(data) ? data : [];
  }, []);

  const {
    columnSelectedValues,
    hasMultiFacetSelection,
    applyFacetClientFilter,
    renderTextColumnFilterTrigger,
  } = useHeatStyleColumnFacets({
    enabled: embeddedInModernLayout,
    filterableColumns: TECH_MODERN_FACET_COLUMNS,
    listCount,
    projectList,
    searchCriteria: modernSearchCriteria,
    columnFilters,
    onColumnFilterChange: handleColumnFilterChange,
    fetchFacetRows: fetchTechnicalFacetRows,
  });

  const modernWideTableActive = useMemo(
    () => embeddedInModernLayout && (modernBulkFilterActive || hasMultiFacetSelection),
    [embeddedInModernLayout, modernBulkFilterActive, hasMultiFacetSelection],
  );

  const [pipeDiameterDisplayUnit, setPipeDiameterDisplayUnit] = useState('mm');
  const [wallThicknessDisplayUnit, setWallThicknessDisplayUnit] = useState('mm');
  const columnList = {
    ID: 'ID',
    Material: 'Material',
    TestType: 'Test Type',
    Vendor: 'Vendor',
    ProjectName: 'Project Name',
    Timeline: 'Timeline',
    Temperature: 'Temperature',
    JointReference: 'Joint Reference',
    WallThickness: 'Wall Thickness',
    PipeDiameter: 'Pipe Diameter',
    Company: 'Company',
    Contractor: 'Contractor',
    FileName: 'File Name',
    ReportNumber: 'Report Number',
    TestedDate: 'Tested Date',
    ReportedDate: 'Report Date',
  };

  const [columnDefs, setColumnDefs] = useState([]);

  const generateColumns = (columnList, onReadParts, lengthUnitState = {}) => {
    const {
      pipeDiameterDisplayUnit = 'mm',
      setPipeDiameterDisplayUnit,
      wallThicknessDisplayUnit = 'mm',
      setWallThicknessDisplayUnit,
    } = lengthUnitState;

    const columnDefs = Object.keys(columnList).map((key) => {
      const baseDef = {
        field: key,
        headerName: columnList[key],
        key,
        cellRenderer: (params) => {
          const isDateColumn = key === 'TestedDate' || key === 'ReportedDate';
          return isDateColumn && params.value
            ? moment(params.value).format('DD-MMM-YYYY')
            : params.value;
        },
        cellStyle: { textAlign: 'center' },
        headerClass: 'ag-center-header',
        filter: 'agMultiColumnFilter',
        filterParams: {
          filters: [
            {
              filter: 'agTextColumnFilter',
              filterParams: {
                filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
                buttons: ['reset'],
              },
            },
            {
              filter: 'agSetColumnFilter',
              filterParams: {
                buttons: ['reset'],
              },
            },
          ],
        },
      };

      if (key === 'PipeDiameter') {
        return {
          ...baseDef,
          valueFormatter: (params) => convertMmToDisplay(params.value, pipeDiameterDisplayUnit),
          filterValueGetter: (params) => convertMmToDisplay(params.data?.PipeDiameter, pipeDiameterDisplayUnit),
          cellRenderer: undefined,
          floatingFilterComponent: LengthUnitFloatingFilter,
          floatingFilterComponentParams: {
            displayUnit: pipeDiameterDisplayUnit,
            onDisplayUnitChange: setPipeDiameterDisplayUnit,
            columnId: key,
          },
        };
      }
      if (key === 'WallThickness') {
        return {
          ...baseDef,
          valueFormatter: (params) => convertMmToDisplay(params.value, wallThicknessDisplayUnit),
          filterValueGetter: (params) => convertMmToDisplay(params.data?.WallThickness, wallThicknessDisplayUnit),
          cellRenderer: undefined,
          floatingFilterComponent: LengthUnitFloatingFilter,
          floatingFilterComponentParams: {
            displayUnit: wallThicknessDisplayUnit,
            onDisplayUnitChange: setWallThicknessDisplayUnit,
            columnId: key,
          },
        };
      }
      return baseDef;
    });
    // Add the actions column at the end
    columnDefs.push({
      headerName: 'Results',
      field: 'results',
      cellRenderer: (params) => {
        const record = params.data;
        const hasSingleItem = getResultsItems(record.Results);
        let itemValue = '';
        // if (hasSingleItem)
        itemValue = getResultsItemsValue(record.Results);
        // console.log(hasSingleItem)
        // console.log(itemValue)
        return (
          <>
            {hasSingleItem || itemValue === '_' ? (
              <a>{itemValue === '_' ? '-' : itemValue}</a>
            ) : (
              <Tooltip title="Results">
                <span
                  className="icon-18 mr-2 cursor-pointer edit-hover"
                  style={{ marginRight: '00px' }}
                >
                  <Eye
                    className="lucide"
                    size={24}
                    strokeWidth={2}
                    onClick={() => onReadParts(record)}
                  />
                </span>
              </Tooltip>
            )}
          </>
        );
      },
      cellStyle: { textAlign: 'center' },
      filter: false, // Disable filtering for the Actions column
    });

    // Add the actions column at the end
    columnDefs.push({
      headerName: 'Actions',
      field: 'action',
      pinned: 'right',
      cellRenderer: (params) => {
        const record = params.data;
        return (
          <div>
            <Tooltip title="View  Report">
              <span
                className="inline-flex cursor-pointer"
                style={{ marginRight: '20px' }}
                role="presentation"
              >
                <FileIcon
                  className="pdfIconRed lucide"
                  size={24}
                  strokeWidth={2}
                  onClick={() => onClickPDFView(record)}
                />
              </span>
            </Tooltip>
            <Tooltip title="Update">
              <span className="icon-18 mr-2 cursor-pointer edit-hover">
                <Pencil
                  className="lucide"
                  size={24}
                  strokeWidth={2}
                  style={{ paddingRight: '0px' }}
                  onClick={() => onUpdateClick(record)}
                />
              </span>
            </Tooltip>
          </div>
        );
      },
      cellStyle: { textAlign: 'center' },
      filter: false, // Disable filtering for the Actions column
    });
    setColumnDefs(columnDefs);

    return columnDefs;
  };

  useEffect(() => {
    console.log('rawDataList updated:', projectList.length);

    if (projectList.length > 0) {
      generateColumns(columnList, onReadParts, {
        pipeDiameterDisplayUnit,
        setPipeDiameterDisplayUnit,
        wallThicknessDisplayUnit,
        setWallThicknessDisplayUnit,
      });
    }
    setRowData(projectList);
  }, [projectList, pipeDiameterDisplayUnit, wallThicknessDisplayUnit]);

  const getResultsItems = (data) => {
    if (!data) return false;
    const dataArray = Object.entries(data).map(([key, value]) => ({
      label: key,
      value: value,
    }));

    if (dataArray && dataArray.length == 1) {
      try {
        //JSON.parse(dataArray[0].value)

        if (typeof dataArray[0].value == 'object') {
          //  console.log('json v', dataArray[0].value, data)
          return false;
        }
      } catch (error) {
        return false;
      }

      return true;
    } else return false;

    // return   typeof data === 'object' && data !== null
    //console.log(dataArray)
    //return dataArray.length == 1;
  };

  const getResultsItemsValue = (data) => {
    if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
      return '_'; // Return '_' if data is null, undefined, not an object, or empty
    }

    const dataArray = Object.entries(data).map(([key, value]) => ({
      label: key || '_', // Use '_' if the key is null or undefined
      value: value !== null && value !== undefined ? value : '_', // Use '_' if the value is null or undefined
    }));

    return dataArray && dataArray.length > 1 ? dataArray : dataArray[0]?.value || '_';
  };

  const onClickPDFView = (record) => {
    // Open PDF viewer in a new tab/window
    window.open(`/TechnicalPage/pdf/${record.ID}`, '_blank');
  };

  useEffect(() => {
    if (embeddedInModernLayout) return;
    var obj = {
      pageSize: 100000,
      pageNumber: 1,
    };
    if (rowData.length == 0) onProjectList(obj);
  }, [embeddedInModernLayout]);

  /* Sync column filters + single-value facets to search key for modern layout API (GetReportList contract). */
  useEffect(() => {
    if (!embeddedInModernLayout) return;
    const extra = {};
    if (filterTestedDate) extra.TestedDate = moment(filterTestedDate).format('YYYY-MM-DD');
    if (filterReportedDate) extra.ReportedDate = moment(filterReportedDate).format('YYYY-MM-DD');

    let criteria = buildCriteriaWithColumnFacets({
      filterableColumns: TECH_MODERN_FACET_COLUMNS,
      columnFilters,
      columnSelectedValues,
      extraCriteria: extra,
    });

    if (criteria.FileName != null && String(criteria.FileName).length) {
      criteria.Filename = criteria.FileName;
      delete criteria.FileName;
    }
    const wtVal = (columnFilters.WallThickness || '').trim();
    const wtFacet = columnSelectedValues.WallThickness;
    if (wtVal && (wtFacet == null || wtFacet.size === 0)) {
      const mm = modernLengthSearchToMm(wtVal, wallThicknessDisplayUnit);
      criteria.WallThickness = mm || wtVal;
    }
    const pdVal = (columnFilters.PipeDiameter || '').trim();
    const pdFacet = columnSelectedValues.PipeDiameter;
    if (pdVal && (pdFacet == null || pdFacet.size === 0)) {
      const mm = modernLengthSearchToMm(pdVal, pipeDiameterDisplayUnit);
      criteria.PipeDiameter = mm || pdVal;
    }

    setModernSearchCriteria((prev) => {
      const a = JSON.stringify(prev);
      const b = JSON.stringify(criteria);
      if (a === b) return prev;
      return criteria;
    });
  }, [
    embeddedInModernLayout,
    columnFilters,
    columnSelectedValues,
    filterTestedDate,
    filterReportedDate,
    wallThicknessDisplayUnit,
    pipeDiameterDisplayUnit,
  ]);

  /* Modern layout: wide fetch when ID/API-ignored columns are filtered or multi-value facets need a full row set. */
  useEffect(() => {
    if (!embeddedInModernLayout) return;
    if (!modernWideTableActive) return;
    const stripped = stripSearchKeyForBulkFetch(modernSearchCriteria);
    onProjectList({
      pageNumber: 1,
      pageSize: 100000,
      searchKey: Object.keys(stripped).length ? stripped : undefined,
    });
  }, [embeddedInModernLayout, modernWideTableActive, modernSearchCriteria]);

  /* Modern layout: server paging when every active filter is handled by GetReportList */
  useEffect(() => {
    if (!embeddedInModernLayout) return;
    if (modernWideTableActive) return;
    onProjectList({
      pageSize: modernPageSize,
      pageNumber: modernPageNumber,
      searchKey: Object.keys(modernSearchCriteria).length ? modernSearchCriteria : undefined,
    });
  }, [embeddedInModernLayout, modernWideTableActive, modernPageNumber, modernPageSize, modernSearchCriteria]);
  // useEffect(() => {

  //     // var obj = {
  //     //     pageSize: currentPagesize,
  //     //     pageNumber: 1,
  //     //     searchKey: searchCriteria
  //     // }
  //     // setCurrent(1)
  //     // onProjectList(obj)

  //     const isvalid = hasNonEmptyTag(searchCriteria)
  //     let obj = {
  //         pageSize: isvalid ? currentPagesize : prevPagesize,
  //         pageNumber: isvalid ? 1 : prevPageNumber,
  //         searchKey: searchCriteria
  //     };
  //     //console.log(obj);

  //     (async () => {
  //         // setSelectedMaterial([])
  //         if (isvalid) {
  //             setPrevPagesize(currentPagesize)
  //             setPrevPageNumber(current)
  //         }
  //         setCurrent(obj.pageNumber)
  //         await onProjectList(obj);
  //     })();

  // }, [searchCriteria]);
  useEffect(() => {
    if (!showAddNewModal) addNewForm.resetFields();
  }, [showAddNewModal]);
  const onReadParts = async (record) => {
    if (record) {
      // console.log(record)
      setSelectedRow(record);
      setResultModalVisible(true);
      //setshowTestPage(true)
    }
  };
  const onUpdateClick = (record) => {
    if (record) {
      // let editData = {
      //     projectName: record.ProjectName,
      //     projectNumber: record.ProjectNumber,

      // };
      addNewForm.setFieldsValue(record);
      addNewForm.setFieldsValue(record.Results);
      //console.log(record)
      seteditRecord(record);
      setIsEdit(true);
      setShowAddNewModal(true);
    }
  };

  const handleSortClick = (columnKey) => {
    setSortField(columnKey);
    setSortOrder((prev) => (sortField === columnKey && prev === 'ascend' ? 'descend' : 'ascend'));
    setModernPageNumber(1);
  };

  /* Client-side sorted data for Test Repository table (backend does not support sort) */
  const sortedProjectList = useMemo(() => {
    if (!embeddedInModernLayout || !projectList || !Array.isArray(projectList) || !sortField || !sortOrder) {
      return projectList ? [...projectList] : [];
    }
    const isDesc = sortOrder === 'descend';
    const dateKeys = ['TestedDate', 'ReportedDate'];
    return [...projectList].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      if (dateKeys.includes(sortField)) {
        aVal = aVal ? moment(aVal).valueOf() : 0;
        bVal = bVal ? moment(bVal).valueOf() : 0;
        return isDesc ? bVal - aVal : aVal - bVal;
      }
      if (sortField === 'ID' && typeof aVal !== 'undefined' && typeof bVal !== 'undefined') {
        const aNum = Number(aVal);
        const bNum = Number(bVal);
        if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) return isDesc ? bNum - aNum : aNum - bNum;
      }
      aVal = aVal == null ? '' : String(aVal);
      bVal = bVal == null ? '' : String(bVal);
      const cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
      return isDesc ? -cmp : cmp;
    });
  }, [embeddedInModernLayout, projectList, sortField, sortOrder]);

  const modernColumnFilteredList = useMemo(() => {
    if (!embeddedInModernLayout || !modernWideTableActive) return sortedProjectList;

    let rows = sortedProjectList;

    if (!modernBulkFilterActive) {
      return applyFacetClientFilter(rows);
    }

    const idNeedle = String(columnFilters.ID || '').trim().toLowerCase();
    if (idNeedle) {
      rows = rows.filter((row) => String(row?.ID ?? '').toLowerCase().includes(idNeedle));
    }

    const jrNeedle = String(columnFilters.JointReference || '').trim();
    if (jrNeedle) {
      const compact = jrNeedle.toLowerCase().replace(/\s+/g, '');
      rows = rows.filter((row) =>
        String(row?.JointReference ?? '')
          .toLowerCase()
          .replace(/\s+/g, '')
          .includes(compact),
      );
    }

    const companyNeedle = String(columnFilters.Company || '').trim().toLowerCase();
    if (companyNeedle) {
      rows = rows.filter((row) => String(row?.Company ?? '').toLowerCase().includes(companyNeedle));
    }

    const contractorNeedle = String(columnFilters.Contractor || '').trim().toLowerCase();
    if (contractorNeedle) {
      rows = rows.filter((row) =>
        String(row?.Contractor ?? '').toLowerCase().includes(contractorNeedle),
      );
    }

    const fileNeedle = String(columnFilters.FileName || '').trim().toLowerCase();
    if (fileNeedle) {
      rows = rows.filter((row) => {
        const name = row?.FileName ?? row?.Filename ?? '';
        return String(name).toLowerCase().includes(fileNeedle);
      });
    }

    const wtNeedle = String(columnFilters.WallThickness || '').trim().toLowerCase();
    if (wtNeedle) {
      rows = rows.filter((row) => {
        const disp = convertMmToDisplay(row?.WallThickness, wallThicknessDisplayUnit);
        const raw = String(row?.WallThickness ?? '').toLowerCase();
        return (disp !== '-' && disp.toLowerCase().includes(wtNeedle)) || raw.includes(wtNeedle);
      });
    }

    const pdNeedle = String(columnFilters.PipeDiameter || '').trim().toLowerCase();
    if (pdNeedle) {
      rows = rows.filter((row) => {
        const disp = convertMmToDisplay(row?.PipeDiameter, pipeDiameterDisplayUnit);
        const raw = String(row?.PipeDiameter ?? '').toLowerCase();
        return (disp !== '-' && disp.toLowerCase().includes(pdNeedle)) || raw.includes(pdNeedle);
      });
    }

    return applyFacetClientFilter(rows);
  }, [
    embeddedInModernLayout,
    modernWideTableActive,
    modernBulkFilterActive,
    sortedProjectList,
    columnFilters,
    wallThicknessDisplayUnit,
    pipeDiameterDisplayUnit,
    applyFacetClientFilter,
  ]);

  const modernTableDataSource = useMemo(() => {
    if (!embeddedInModernLayout) return sortedProjectList;
    if (!modernWideTableActive) return sortedProjectList;
    const start = (modernPageNumber - 1) * modernPageSize;
    return modernColumnFilteredList.slice(start, start + modernPageSize);
  }, [
    embeddedInModernLayout,
    modernWideTableActive,
    modernColumnFilteredList,
    modernPageNumber,
    modernPageSize,
    sortedProjectList,
  ]);

  useEffect(() => {
    if (!embeddedInModernLayout || !modernWideTableActive) return;
    const total = modernColumnFilteredList.length;
    const maxPage = Math.max(1, Math.ceil(total / modernPageSize) || 1);
    if (modernPageNumber > maxPage) setModernPageNumber(maxPage);
  }, [
    embeddedInModernLayout,
    modernWideTableActive,
    modernColumnFilteredList.length,
    modernPageSize,
    modernPageNumber,
  ]);

  /* Modern layout: Ant Design Table columns with two-row header and per-column filters (same as Heat Profile / DCAL) */
  const textFilterKeys = TECH_MODERN_FACET_COLUMNS;
  const modernColumnsWithFilters = useMemo(() => {
    const baseCols = [
      ...Object.keys(columnList).map((key) => ({
        title: columnList[key],
        dataIndex: key,
        key,
        align: 'left',
        render: (val, record) => {
          if (key === 'TestedDate' || key === 'ReportedDate') {
            return val ? moment(val).format('DD-MMM-YYYY') : '-';
          }
          if (key === 'PipeDiameter') {
            return convertMmToDisplay(val, pipeDiameterDisplayUnit);
          }
          if (key === 'WallThickness') {
            return convertMmToDisplay(val, wallThicknessDisplayUnit);
          }
          return val === undefined || val === null || val === '' ? '-' : String(val);
        },
      })),
      {
        title: 'Results',
        dataIndex: 'results',
        key: 'results',
        align: 'center',
        width: 100,
        render: (_, record) => {
          const hasSingleItem = getResultsItems(record.Results);
          const itemValue = getResultsItemsValue(record.Results);
          return hasSingleItem || itemValue === '_' ? (
            <span>{itemValue === '_' ? '-' : itemValue}</span>
          ) : (
            <Tooltip title="Results">
              <span className="heat-profile-actions-cell">
                <Eye
                  className="txtIcon lucide"
                  size={24}
                  strokeWidth={2}
                  onClick={() => onReadParts(record)}
                />
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: 'Actions',
        dataIndex: 'action',
        key: 'action',
        align: 'center',
        width: 120,
        render: (_, record) => (
          <div className="heat-profile-actions-cell">
            <Tooltip title="View Report">
              <FileIcon
                className="txtIcon pdfIconRed lucide"
                size={24}
                strokeWidth={2}
                onClick={() => onClickPDFView(record)}
              />
            </Tooltip>
            <Tooltip title="Update">
              <Pencil
                className="txtIcon lucide"
                size={24}
                strokeWidth={2}
                onClick={() => onUpdateClick(record)}
              />
            </Tooltip>
          </div>
        ),
      },
    ];
    const titles = [
      'ID', 'MATERIAL', 'TEST TYPE', 'VENDOR', 'PROJECT NAME', 'TIMELINE',
      'TEMPERATURE', 'JOINT REFERENCE', 'WALL THICKNESS', 'PIPE DIAMETER', 'COMPANY', 'CONTRACTOR', 'FILE NAME', 'REPORT NUMBER',
      'TESTED DATE', 'REPORT DATE', 'RESULTS', 'ACTIONS',
    ];
    /* Min widths so header label + sort icon + gap fit without truncation (production-ready) */
    const columnMinWidths = {
      ID: 150,
      Material: 200,
      TestType: 200,
      Vendor: 180,
      ProjectName: 200,
      Timeline: 180,
      Temperature: 180,
      JointReference: 200,
      WallThickness: 200,
      PipeDiameter: 200,
      Company: 180,
      Contractor: 180,
      FileName: 220,
      ReportNumber: 220,
      TestedDate: 200,
      ReportedDate: 200,
      results: 100,
      action: 120,
    };
    return baseCols.map((col, idx) => {
      const dataIndex = col.dataIndex;
      const isResults = dataIndex === 'results';
      const isActions = dataIndex === 'action';
      const isTestedDate = dataIndex === 'TestedDate';
      const isReportedDate = dataIndex === 'ReportedDate';
      const isTextFilter = textFilterKeys.includes(dataIndex);
      let title = titles[idx] || col.title;
      if (isTextFilter) {
        title = (
          <div className="column-header-with-filter">
            <div className="column-header-text-wrapper">
              <span>{titles[idx]}</span>
              <span
                className="custom-sort-icon"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSortClick(dataIndex); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSortClick(dataIndex); } }}
              >
                {sortField === dataIndex && sortOrder === 'ascend' && <CaretUpOutlined />}
                {sortField === dataIndex && sortOrder === 'descend' && <CaretDownOutlined />}
                {(!sortField || sortField !== dataIndex || !sortOrder) && (
                  <span className="sort-icon-placeholder"><CaretUpOutlined /></span>
                )}
              </span>
              {renderTextColumnFilterTrigger(dataIndex)}
            </div>
            <div className="header-filter-input">
              <div className="heat-profile-inline-field-shell heat-profile-inline-field-shell--sm heat-profile-header-inline-shell">
                <Input
                  placeholder="Search"
                  size="small"
                  value={columnFilters[dataIndex] || ''}
                  onChange={(e) => handleColumnFilterChange(dataIndex, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          </div>
        );
      } else if (isTestedDate) {
        title = (
          <div className="column-header-with-filter">
            <div className="column-header-text-wrapper">
              <span>TESTED DATE</span>
              <span
                className="custom-sort-icon"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSortClick('TestedDate'); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSortClick('TestedDate'); } }}
              >
                {sortField === 'TestedDate' && sortOrder === 'ascend' && <CaretUpOutlined />}
                {sortField === 'TestedDate' && sortOrder === 'descend' && <CaretDownOutlined />}
                {(!sortField || sortField !== 'TestedDate' || !sortOrder) && (
                  <span className="sort-icon-placeholder"><CaretUpOutlined /></span>
                )}
              </span>
            </div>
            <div className="header-filter-input">
              <DatePicker
                className="heat-profile-header-date-picker"
                placeholder="Select date"
                size="small"
                format="DD-MMM-YYYY"
                value={filterTestedDate}
                onChange={(date) => { setFilterTestedDate(date); if (embeddedInModernLayout) setModernPageNumber(1); }}
                onClick={(e) => e.stopPropagation()}
                suffixIcon={
                  filterTestedDate ? (
                    <CloseOutlined
                      className="heat-profile-date-clear-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterTestedDate(null);
                      }}
                    />
                  ) : (
                    <CalendarOutlined className="heat-profile-date-icon" />
                  )
                }
                dropdownClassName="heat-profile-datepicker-dropdown"
                allowClear={false}
              />
            </div>
          </div>
        );
      } else if (isReportedDate) {
        title = (
          <div className="column-header-with-filter">
            <div className="column-header-text-wrapper">
              <span>REPORT DATE</span>
              <span
                className="custom-sort-icon"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleSortClick('ReportedDate'); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSortClick('ReportedDate'); } }}
              >
                {sortField === 'ReportedDate' && sortOrder === 'ascend' && <CaretUpOutlined />}
                {sortField === 'ReportedDate' && sortOrder === 'descend' && <CaretDownOutlined />}
                {(!sortField || sortField !== 'ReportedDate' || !sortOrder) && (
                  <span className="sort-icon-placeholder"><CaretUpOutlined /></span>
                )}
              </span>
            </div>
            <div className="header-filter-input">
              <DatePicker
                className="heat-profile-header-date-picker"
                placeholder="Select date"
                size="small"
                format="DD-MMM-YYYY"
                value={filterReportedDate}
                onChange={(date) => { setFilterReportedDate(date); if (embeddedInModernLayout) setModernPageNumber(1); }}
                onClick={(e) => e.stopPropagation()}
                suffixIcon={
                  filterReportedDate ? (
                    <CloseOutlined
                      className="heat-profile-date-clear-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterReportedDate(null);
                      }}
                    />
                  ) : (
                    <CalendarOutlined className="heat-profile-date-icon" />
                  )
                }
                dropdownClassName="heat-profile-datepicker-dropdown"
                allowClear={false}
              />
            </div>
          </div>
        );
      }
      const isId = dataIndex === 'ID';
      const isSortable = !isResults && !isActions;
      const minW = columnMinWidths[dataIndex];
      return {
        ...col,
        title,
        align: isId ? 'left' : col.align,
        width: minW,
        minWidth: minW,
        ...(isSortable && { sorter: true, sortOrder: sortField === dataIndex ? sortOrder : undefined }),
      };
    });
  }, [
    columnFilters,
    filterTestedDate,
    filterReportedDate,
    embeddedInModernLayout,
    sortField,
    sortOrder,
    handleColumnFilterChange,
    handleSortClick,
    renderTextColumnFilterTrigger,
  ]);
  // useEffect(() => {
  //     if (editRecord)
  //         setShowAddNewModal(true)

  // }, [editRecord])

  function hasNonEmptyTag(jsonObj) {
    for (const key in jsonObj) {
      if (jsonObj.hasOwnProperty(key)) {
        const value = jsonObj[key];
        console.log(jsonObj);

        if (Array.isArray(value) && value.length > 0) {
          return true;
        } else if (typeof value === 'string' && value.trim() !== '') {
          return true;
        }
      }
    }
    return false;
  }

  const renderKeyValuePairs = (obj, level = 0) => {
    return Object.entries(obj).map(([key, value]) => {
      if (value !== '_') {
        if (typeof value === 'object' && value !== null) {
          // Recursively render nested objects
          return (
            <div
              key={key}
              style={{ marginLeft: `${level * 20}px` }}
            >
              <strong>{key}</strong>:{renderKeyValuePairs(value, level + 1)}
            </div>
          );
        } else {
          // Render the key-value pair
          return (
            <div
              key={key}
              style={{ marginLeft: `${level * 20}px` }}
            >
              <strong>{key}</strong>: {value}
            </div>
          );
        }
      }
      return null;
    });
  };
  const addDropdownOptions = async (worksheet, columnLetter, options, startRow = 2) => {
    let optionsSheet = worksheet.workbook.getWorksheet('Options');

    if (!optionsSheet) {
      optionsSheet = worksheet.workbook.addWorksheet('Options');
      optionsSheet.state = 'veryHidden';
    }

    options.forEach((value, index) => {
      optionsSheet.getCell(`${columnLetter}${index + startRow}`).value = value;
    });

    for (let i = startRow; i < 1000; i++) {
      worksheet.getCell(`${columnLetter}${i}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`=Options!$${columnLetter}$1:$${columnLetter}$9999`],
      };
    }
  };
  const handleDownloadFilterTable = async (filterData) => {
    setExportLoading(true);
    //console.log(filterData)
    const data = await createExcelTemplate();
    const blob = new Blob([data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Coating Technical Report.xlsx';
    link.click();
    setExportLoading(false);
  };

  const createExcelTemplate = async () => {
    var obj = {
      searchKey: searchCriteria,
    };
    const data = await getExportDataListFromDatabse(obj);
    if (data && data.length > 0) {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Coating Technical Report');

      // Keys to skip
      const keysToSkip = ['ID', 'FileLink', 'totalCount', 'CreationDate', 'UpdatedDate'];

      // Function to flatten the object structure
      const flattenObject = (obj, parentKey = '', result = {}) => {
        for (const key in obj) {
          const propName = parentKey ? `${parentKey}_${key}` : key;
          if (keysToSkip.includes(key)) continue;
          if (typeof obj[key] === 'object' && obj[key] !== null && !Array.isArray(obj[key])) {
            flattenObject(obj[key], propName, result);
          } else {
            result[propName] = obj[key];
          }
        }
        return result;
      };

      const headers = new Set();
      const rows = [];

      data.forEach((item) => {
        const flattenedItem = flattenObject(item);
        Object.keys(flattenedItem).forEach((key) => {
          if (!keysToSkip.includes(key)) headers.add(key);
        });
        rows.push(flattenedItem);
      });

      const orderedHeaders = Array.from(headers);
      Array.prototype.myJoin = function (seperator, start, end) {
        if (!start) start = 0;
        if (!end) end = this.length - 1;
        end++;
        return this.slice(start, end).join(seperator);
      };
      // Define columns with hierarchical headers
      sheet.columns = orderedHeaders.map((header) => {
        if (header === 'Results_Result') {
          return { header: 'Results', key: header, width: 20 };
        } else if (header.startsWith('Results_Result')) {
          const headerParts = header.split('_');
          return { header: headerParts.myJoin(' -> ', 1, 2), key: header, width: 20 };
        } else {
          const headerParts = header.split('_');
          return { header: headerParts.join(' -> '), key: header, width: 20 };
        }
      });

      // Print the converted object
      //console.log('Converted Object', orderedHeaders, rows);

      // Add rows
      rows.forEach((rowData, index) => {
        const row = sheet.getRow(index + 2); // Start from the second row
        const orderedRowData = orderedHeaders.map((header) =>
          rowData[header] !== undefined ? rowData[header] : ''
        );
        orderedRowData.forEach((value, cellIndex) => {
          row.getCell(cellIndex + 1).value = value;
        });
        // console.log(row.values);
      });
      // Save the workbook to a buffer
      return await workbook.xlsx.writeBuffer();
    } else {
      notification.error({
        message: 'Filter data empty',
      });
    }
  };

  const handleDownloadSGSFrontSheetTemplate = async () => {
    const workbook = new ExcelJS.Workbook();
    const addStationSheet = workbook.addWorksheet('SGS front sheet');

    const headerRow = addStationSheet.getRow(1);
    headerRow.height = 40;

    const projectNames = [
      'Askeladd',
      'Askellad',
      'Bahr Essalam',
      'Barzan Onshore',
      'BPAKER KEG',
      'Colibri',
      'Culzean',
      'Edradour Glenlivet',
      'Fenja Development',
      'Future Growth Project',
      'Golfinho',
      'Greater Western Flank II',
      'GWF II Spools',
      'Ichthys',
      'Ichthys Spools',
      'Julimar',
      'Kaombo Flowlines',
      'Khazzan Galfar',
      'Lingshui',
      'Mero 1',
      'Mero 2',
      'Moheshkali Floating LNG',
      'NAM K15 - L13',
      'NFA Field Joints',
      'NFA Spools & Risers',
      'NFPS',
      'Peregrino 2 Surf',
      'Pupp Idoho',
      'Ras Markaz',
      'Rosehill',
      'Rota 3 - Tramo 2',
      'Scarborough',
      'Sepia',
      'Sepia 3LPP Onshore Tie-Ins',
      'Sepia 3LPP Post Bend',
      'TAP',
      'Technip Norge AS',
      'Tolmount',
      'Tortue PVT',
      'West Nile Delta',
      'Zinia',
    ];
    const testType = [
      'Hot Water Immersion',
      'Post-Bend Microscopic Analysis',
      'Thermal Conductivity',
      'Abrasion Test',
      'Adhesion',
      'Bond Test',
      'Brittleness Temperature',
      'Buchholz Hardness',
      'Cathodic Disbondment',
      'Coefficient Expansion',
      'Compressive strength',
      'Density',
      'Determination of Cure Rate',
      'Determination of Glass Transistion Temperature(Tg)',
      'Determination of Tensile Properties',
      'Di-electric Strength',
      'Dry and Saturated Density',
      'Dry Density',
      'FBE Adhesion',
      'FBE Visual Assessment',
      'Flexibility',
      'Flexural Modulus',
      'Hardness',
      'Heat Capacity',
      'Hot Water Immersion Post-Exposure',
      'Hot Water Soak',
      'Hot Water Soak + Pull-Off Adhesion',
      'Impact Resistance',
      'Indentation Resistance',
      'Interface Contamination',
      'Lap Shear',
      'Micro Exam of Cracks',
      'Multiple Test',
      'Oxidation Induction Time',
      'Peel Adhesion',
      'Peel Adhesion Post Hot Water Immersion',
      'Peel Steel',
      'Peel Weld',
      'Porosity',
      'Post Bend Tensile Adhesion',
      'Pre-Exposure X-Cut',
      'Primer Viscosity Analysis',
      'Pull Off Adhesion',
      'Pull off Adhesion Post Hot Water Immersion',
      'Pull off Adhesion to Overlap',
      'Pull off Adhesion to Steel',
      'Pull-Off Adhesion',
      'Ring Shear',
      'Sand Abrasion',
      'Saturated Density',
      'Sectioning',
      'Shore D Hardness',
      'Softening Point',
      'Specific Electrical Insulation Resistance',
      'Specific Heat Capacity',
      'Taber Abrasion',
      'Tensile Adhesion',
      'Tensile Adhesion Post Hot Water Immersion',
      'Tensile Properties',
      'Tg Analysis',
      'Thermal Ageing',
      'Thermal Ageing and Post Exposure Test',
      'Thermal Analysis',
      'Thermal Degradation',
      'Tri-Axial Compression Creep',
      'Vicat Softening',
      'Visual Assessment',
      'Volume Resistivity',
      'Water Absorption',
    ];
    const Company = [
      'ArcelorMittal Tubular Products Aktau',
      'Bonatti Tengiz Republic of Kazakhstan',
      'BP',
      'COEEC',
      'COOEC',
      'Equinor',
      'Excellerate Energy',
      'Inpex',
      'Maersk',
      'MPN',
      'Neptune',
      'OTTCO',
      'Petrobras',
      'Qatar Gas',
      'Qatar Petroleum',
      'QG',
      'QP',
      'Shell',
      'TCO',
      'Total',
      'Total E&P UK Limited',
      'Trans Adriatic Pipeline AG',
      'Unknown',
      'Woodside',
    ];
    const contractor = [
      'Bonatti J&P Avax S.r.l JV',
      'COOEC',
      'CPP',
      'CRCE',
      'Galfar',
      'Geocean',
      'Heerema Marine Contractors',
      'KTR',
      'McDermott',
      'McDermott Eastern Hemisphere Ltd',
      'Saipem',
      'Subsea 7',
      'Subsea7',
      'Technip',
      'Technip Norge AS',
      'TechnipFMC',
      'Unknown',
      'Van Oord',
    ];
    const material1 = [
      '3M scotchkote 226N',
      '3M Scotchkote 226N FBE',
      '3M Scotchkote 626-40 FBE',
      'Akzo Nobel Resicoat R-726 LAT FBE',
      'Axalta Nap-Gard 7-2500 FBE',
      'BASF H2100/71 HDPUF',
      'BASF H2100/78',
      'BASF H2100/78 Polyol',
      'Borealis Borcoat BB127E',
      'Borealis Borcoat BB127E-PW',
      'Borealis Borcoat EA165E PP',
      'Borealis EA165E IMPP',
      'Borealis EA165E PP',
      'Canusa CPS Liquid Epoxy',
      'Canusa GTS 65 HSS',
      'Canusa GTS PP HSS',
      'Canusa GTS-PP',
      'Canusa GTS-PP 100 HSS',
      'Canusa HBE -OS',
      'Canusa KLNN Heat Shrink Sleeve',
      'Canusa Liquid Epoxy E-Primer',
      'Canusa Liquid Epoxy Primer',
      'Canusa MIS 65 HSS',
      'Canusa P Primer',
      'Canusa P-Primer',
      'CMPP HX EP5 10/60 M Bianco',
      'FBE - Scotchcote 226N',
      'FBE Jotupipe-LT7011',
      'High Density Polyurethane Foam',
      'Jotapipe AC 1003 FBE',
      'Jotupipe-LT7011 FBE',
      'Pipeclad 2000',
      'Pipeclad 2000 FBE',
      'Pipeclad 2000 Slow Gel FBE',
      'Pipeclad 2000 Sloww Gel FBE',
      'Protegol 32-55 PN Comp A',
      'Protegol 32-55 PN_M-L Comp A',
      'Protegol 32-60 Comp A',
      'Safoam PN-40-E',
      'Scotchkote 226N FBE',
      'Sherwin Williams HOT120 Flex FBE',
      'Sherwin Williams HOT150 Flex FBE',
      'Valspar HOT 120 FBE',
      'Valspar Hot 150',
      'Valspar Pipeclad 2000 FBE',
      'Valspar Pipeclad 2000 Slow Gel FBE',
      'Valspar Pipeclad HOT 150',
      'Valspar Pipeclad Hot 150 FBE',
      'Valspar Pipeclad HOT 150 Flex FBE',
      'Valspar Pipeclad LAT 2000',
    ];
    const material2 = [
      'Basell Hifax 10/60M',
      'Basell Hifax EP5 10/60E PP Tape',
      'Borealis BB127E CMPP',
      'Hifax Bianco EP5 10/60M CMPP',
      'Hifax EPS 10/60M Bianco',
      'Rosehill RS 3173 IMPU',
      'Rosehill RS 3604 IMPU',
      '342Y081704',
      'Axalta Nap-Gard 7-2504 FBE',
      'Basell 10/60 EP 10-60M',
      'Basell Hifax 10/60M',
      'Basell Hifax EP5 10/60E PP Tape',
      'Basell Hifax EPS 10/6E PP Tape',
      'BASF PMDI 92140',
      'BASF PMDI 92140 Iso',
      'Borcoat BB127E-PW CMPP',
      'Borealis BB127E CMPP',
      'Borealis BB127E-PW CMPP',
      'Borealis Borcoat BB127E',
      'Borealis Borcoat BB127E-PW CMPP',
      'Borealis EA165E IMPP',
      'Borealis EP5 10/60M CMPP',
      'Borealis EPR 60 Bianco CMPP',
      'Borealis EPR 60 Bianco FSPP',
      'Borocaot BB127E-PW CMPP',
      'Canusa E Primer',
      'Canusa E-Primer',
      'Canusa GTS PP',
      'Canusa GTS PP HSS',
      'Canusa GTS-PP Heat Shrink Sleeve',
      'Canusa GTS-PP HSS',
      'CMPP HX EP5 10/60 M Bianco',
      'ColourHouse CUS1751',
      'Dow Hyperlast FJ 7004 Polyol',
      'EP5 10/60 M CMPP',
      'EP5 10/60 M Flame Spray',
      'EP5 10/60 M Tape Wrap',
      'EPS 10/60M CMPP',
      'Flexion RS3060 IMPU',
      'GTS-PP200-450 WS BK/S Heat Shrink Sleeve',
      'Hifax Bianco EP5 10/60M CMPP',
      'LyondellBasell PP HX EPS 10/60 M',
      'LyondellBasell Hifax EP5/60M PP',
      'Protegol 32-55 PN Comp B',
      'Protegol 32-55 PN_M-L Comp B',
      'Protegol 32-60 Comp B',
      'Reedy Chemicals Safoam PN 40-E',
      'Rosehill Flexilon C35467',
      'Rosehill Flexilon R3173',
      'Safoam PN40-E Foaming Agent',
      'Safoam PN-40-E Foaming Agent',
      'Valspar Pipeclad 2040 Flex LAT',
    ];
    const material3 = [
      'Borealis EA165E IMPP',
      'Rosehill FLEXILON i3173',
      'Rosehill FLEXILON i3604',
      'Basell 10/60 EP 5',
      'Borealis Borcoat EA165E PP',
      'Borealis EA165E',
      'Borealis EA165E IMPP',
      'Borealis EA165E PP',
      'Borealis EA165E PP Pellets',
      'Borealis EP5 10/60 PP Tape',
      'Borealis EP5 10/60E PP Tape',
      'Borealis EP5 10/60M PP Flamespray',
      'Canusa GTS-PP Heat Shrink Sleeve',
      'CUS 1751 Pigment',
      'CUS1751 Pigment',
      'Dow Hyperlast FJ 6609 Iso',
      'EP5 10/60 Bianco PP Tape',
      'EPS 10/60M PP Flamespray',
      'GTS-PP HSS',
      'GTS-PP200-450 WS BK/S Heat',
      'Reedy Chemicals Safoam PN 40-E',
      'Rosehill I3173',
    ];
    const material4 = [
      'Rosehill FLEXILON C3173',
      'Rosehill FLEXILON C3604',
      '_',
      'Borcoat EA165E IMPP',
      'Borealis Borcoat EA165E PP',
      'Borealis EP5 10/60E PP Tape',
      'Safoam PN40-E Foaming Agent',
      'Safoam PN-40E Foaming Agent',
      'Shrink Sleeve',
    ];
    const material5 = ['CUS1751 Pigment', 'Normatch 26-9124MB Pigment'];
    const temperature = [
      '-30 Deg c',
      '-20 Deg c',
      '-20 to 140 Deg c',
      '-5 Deg c',
      '0 Deg c',
      '5 Deg c',
      '10 to -30 Dec c',
      '20 Deg c',
      '21 Deg c',
      '23 Deg c',
      '23.3 Deg C',
      '23.6 Deg c',
      '23.8 Deg C',
      '24 Deg c',
      '25 Deg c',
      '30 Deg c',
      '50 Deg c',
      '55 Deg c',
      '60 Deg c',
      '63 Deg c',
      '65 Deg c',
      '66 Deg c',
      '80 Deg c',
      '81 Deg c',
      '84 Deg c',
      '85 Deg c',
      '90 Deg c',
      '94 Deg c',
      '95 Deg c',
      '100 Deg c',
      '101 Deg c',
      '104 Deg c',
      '110 Deg c',
      '120 Deg C',
      '121 Deg C',
      '134 Deg c',
      '140 Deg c',
      '150 Deg C',
      '180 Deg c',
    ];
    const timeline = [
      '6 Hours',
      '14 Days',
      '15 Mins',
      '16 Hours',
      '21 Days',
      '24 Hours',
      '28 Days',
      '30 Days',
      '40 Hours',
      '48 Hours',
      '49 Hours',
      '60 Days',
      '100 Days',
      '100 Hours',
      '168 Hours',
      '7 Days',
    ];
    const batchNumber1 = [
      '042Y042226',
      '052Y042653',
      '072Y043853',
      '0T2Y047824',
      '0V2Y048112',
      '0W2Y049878',
      '112Y052339',
      '122Y052339',
      '142Y054329',
      '1616402-1',
      '17-B-18',
      '19-A-682',
      '22-B-1307',
      '242Y070303',
      '342Y081704',
      '342Y081706',
      '501084',
      '5J11A-31A',
      '5T36033613',
      '608018',
      '608024A',
      '62108002-4',
      '73200187',
      '842Y020754',
      '8V2Y027986',
      '912Y029599',
      'B-61902006',
      'H61707008-7',
      'HV26002006',
      'HV61707008',
      'HV61902002',
      'HV61912002',
      'HV62002006',
      'IN02 / 4L10 (804)',
      'IN02 5E14',
      'IN02 5L02',
      'IN02 6E20',
      'IN-7E13-1',
      'IN-7K22-3',
      'IN-9B15B-2',
      'IN8J29B/1',
      'OV2YO48112',
      'QA-11-B-1716LG',
      'QA-11-B1716LG.0',
      'QA-16-B-1874',
      'QA-17-A-145',
      'QA-17-B-875',
      'S225195002',
      'VB12994RE',
      'XD291HXAF',
    ];
    const batchNumber2 = [
      '0W2Y049876',
      '10L19A90H0',
      '15-B-959',
      '1616042',
      '1616042-1',
      '1617072-2',
      '16989',
      '17-B-2906',
      '18-B-1317',
      '19-A-682',
      '19-A-743',
      '20-A-66',
      '21-A-484',
      '311-9647',
      '311-9653',
      '4053728',
      '706014B',
      '832-4485',
      '849-4977',
      'AB151HXAA',
      'C51908002',
      'CP11405067',
      'F368J1F001',
      'ISO 1288014',
      'P1DF003670',
      'PL101HX01',
      'RA61612014 02334',
      'RC01 HX01',
      'RF2441E02',
      'RG151HXAD',
      'S224206011',
      'S224612024',
      'S225195002',
      'S225242005',
      'S2255195002',
      'SRG151HXAD',
      'UE021 HXAB',
      'XB181HXAA',
      'XD291',
      'XD291HXAF',
      'XE151HXAG',
    ];
    const batchNumber3 = [
      '14253',
      '16403',
      '311-9650',
      '311-9655',
      '73160601',
      '63151008',
      '73150400',
      '73160863',
      '73200187',
      '73200641',
      '849-4978',
      '1616042-1',
      '18-B-1317',
      'F368J1E015',
      'NH1341E01',
      'PL101H01',
      'PL101HX01',
      'Poly 1267960',
      'QA-2-A-52',
      'RF2441E02',
      'WK2541E48',
      'XE151HXAG',
      'XK0441E56',
    ];
    const batchNumber4 = [
      '73200187',
      '21-A-484',
      '1614091-1',
      '1617072-2',
      '1617072-2',
      '311-9646',
      '311-9651',
      'RF2441E02',
    ];
    const batchNumber5 = ['14253', '5382-001'];

    // Convert image to base64 string
    const getBase64FromImage = (imgUrl) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const dataURL = canvas.toDataURL('image/png');
          resolve(dataURL.split(',')[1]);
        };
        img.onerror = reject;
        img.src = imgUrl;
      });
    };

    // Add logo to the worksheet
    const base64Image = await getBase64FromImage(logoCrc);
    const imageId = workbook.addImage({
      base64: base64Image,
      extension: 'png',
    });

    // Add image with specific positioning and size
    addStationSheet.addImage(imageId, {
      tl: { col: 0, row: 0 },
      br: { col: 2, row: 1 },
      editAs: 'oneCell',
    });

    // Add title row and merge cells
    const titleRow = addStationSheet.getRow(1);
    titleRow.height = 40;

    // First clear any existing content in the title row
    titleRow.eachCell((cell) => {
      cell.value = null;
    });

    // Merge cells for title
    addStationSheet.mergeCells('A1:V1');

    // Set title cell properties
    const titleCell = titleRow.getCell(1);
    titleCell.value = 'SGS Form';
    titleCell.font = {
      size: 16,
      bold: true,
      name: 'Arial',
    };
    titleCell.alignment = {
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
    titleCell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFE0E0E0' }, // Light gray background
    };

    // Ensure the title is visible
    addStationSheet.getColumn(1).width = 20;

    // Add column headers in row 2
    const columnHeaderRow = addStationSheet.getRow(2);
    columnHeaderRow.height = 40;

    // Clear any existing content in row 2
    columnHeaderRow.eachCell((cell) => {
      cell.value = null;
    });

    // Define columns with headers
    const columns = [
      { header: 'File Name', key: 'FileName', width: 20 },
      { header: 'Project Name', key: 'projectName', width: 20 },
      { header: 'Test Type', key: 'TestType', width: 20 },
      { header: 'Time line', key: 'timeline', width: 15 },
      { header: 'Temperature', key: 'temperature', width: 15 },
      { header: 'Company', key: 'company', width: 20 },
      { header: 'Contractor', key: 'contractor', width: 20 },
      { header: 'Report Number', key: 'reportNumber', width: 15 },
      { header: 'Purchase Order', key: 'purchaseOrder', width: 15 },
      { header: 'Results', key: 'results', width: 15 },
      { header: 'Tested Date', key: 'testedDate', width: 15 },
      { header: 'Reported Date', key: 'reportedDate', width: 15 },
      { header: 'Material 1', key: 'material1', width: 20 },
      { header: 'Batch Number 1', key: 'batchNumber1', width: 15 },
      { header: 'Material 2', key: 'material2', width: 20 },
      { header: 'Batch Number 2', key: 'batchNumber2', width: 15 },
      { header: 'Material 3', key: 'material3', width: 20 },
      { header: 'Batch Number 3', key: 'batchNumber3', width: 15 },
      { header: 'Material 4', key: 'material4', width: 20 },
      { header: 'Batch Number 4', key: 'batchNumber4', width: 15 },
      { header: 'Material 5', key: 'material5', width: 20 },
      { header: 'Batch Number 5', key: 'batchNumber5', width: 15 },
    ];

    // Set columns and apply headers to row 2
    addStationSheet.columns = columns;

    // Apply headers to row 2
    columns.forEach((col, index) => {
      const cell = columnHeaderRow.getCell(col.key);
      cell.value = col.header;
      cell.font = {
        bold: true,
        color: { argb: 'FFFFFFFF' }, // White text
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEF314C' }, // Added FF for full opacity
      };
    });

    // Ensure all cells in row 2 are properly formatted
    for (let i = 1; i <= columns.length; i++) {
      const cell = columnHeaderRow.getCell(i);
      if (!cell.value) {
        cell.value = columns[i - 1].header;
        cell.font = {
          bold: true,
          color: { argb: 'FFFFFFFF' }, // White text
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFEF314C' }, // Added FF for full opacity
        };
      }
    }

    // Update dropdown options to start from row 3
    await addDropdownOptions(addStationSheet, 'B', projectNames, 3);
    await addDropdownOptions(addStationSheet, 'C', testType, 3);
    await addDropdownOptions(addStationSheet, 'D', timeline, 3);
    await addDropdownOptions(addStationSheet, 'E', temperature, 3);
    await addDropdownOptions(addStationSheet, 'F', Company, 3);
    await addDropdownOptions(addStationSheet, 'G', contractor, 3);
    await addDropdownOptions(addStationSheet, 'M', material1, 3);
    await addDropdownOptions(addStationSheet, 'N', batchNumber1, 3);
    await addDropdownOptions(addStationSheet, 'O', material2, 3);
    await addDropdownOptions(addStationSheet, 'P', batchNumber2, 3);
    await addDropdownOptions(addStationSheet, 'Q', material3, 3);
    await addDropdownOptions(addStationSheet, 'R', batchNumber3, 3);
    await addDropdownOptions(addStationSheet, 'S', material4, 3);
    await addDropdownOptions(addStationSheet, 'T', batchNumber4, 3);
    await addDropdownOptions(addStationSheet, 'U', material5, 3);
    await addDropdownOptions(addStationSheet, 'V', batchNumber5, 3);

    // Generate and download Excel file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'SGS Front Sheet.xlsx';
    link.click();
  };

  const onRowDragEnd = (event) => {
    const { node, overNode } = event;
    if (!overNode) return;

    const draggedRow = node.data;
    const fromIndex = node.rowIndex;
    const toIndex = overNode.rowIndex;

    if (fromIndex === toIndex) return;

    const newRowData = [...rowData];
    newRowData.splice(fromIndex, 1);
    newRowData.splice(toIndex, 0, draggedRow);

    setRowData(newRowData);
  };

  const gridStyle = useMemo(() => ({ height: '100%', width: '100%' }), []);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
      minWidth: 200,
      floatingFilter: true,
      resizable: true,
      enableValue: true,
      enableRowGroup: true,
      enablePivot: true,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agTextColumnFilter',
            filterParams: {
              filterOptions: ['contains', 'equals', 'startsWith', 'endsWith'],
              buttons: ['reset'],
            },
          },
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              buttons: ['reset'],
            },
          },
        ],
      },
      headerClass: 'ag-center-header',
      cellStyle: { textAlign: 'center' },
    };
  }, []);
  const activeKeyLine = 'Coating Test Repository';

  const defaultExcelExportParams = useMemo(() => {
    return {
      fileName: `${activeKeyLine}.xlsx`, // Default file name
      allColumns: true, // Include all columns by default
      onlySelected: false, // Include all rows, not just selected ones
      sheetName: 'Sheet1', // Default sheet name
    };
  }, [activeKeyLine]);
  const defaultCsvExportParams = useMemo(() => {
    return {
      fileName: `${activeKeyLine}.csv`, // Default file name
      allColumns: true, // Include all columns by default
      onlySelected: false, // Include all rows, not just selected ones
    };
  }, [activeKeyLine]);

  const rowSelection = useMemo(() => {
    return {
      mode: 'singleRow',
      checkboxes: false,
      enableClickSelection: true,
    };
  }, []);

  const onGridReady = useCallback(
    (params) => {
      console.log('on Called get ready', isLoading);
      if (isLoading) {
        params.api.showLoadingOverlay();
      } else {
        params.api.hideOverlay();
      }
    },
    [isLoading]
  );

  // // Update loading state
  // useEffect(() => {
  //     if (gridRef.current && gridRef.current.api) {
  //         if (isLoading) {
  //             gridRef.current.api.showLoadingOverlay();
  //         } else {
  //             gridRef.current.api.hideOverlay();
  //         }
  //     }
  // }, [isLoading]);

  // // Update loading state
  // useEffect(() => {
  //     if (gridRef.current && gridRef.current.api) {
  //         if (isLoading) {
  //             gridRef.current.api.showLoadingOverlay();
  //         } else {
  //             gridRef.current.api.hideOverlay();
  //         }
  //     }
  // }, [isLoading]);

  const [statusBar] = useState({
    statusPanels: [
      {
        statusPanel: 'agTotalRowCountComponent',
        align: 'left',
      },
      {
        statusPanel: 'agSelectedRowCountComponent',
        align: 'left',
      },
      {
        statusPanel: 'agFilteredRowCountComponent',
        align: 'left',
      },
    ],
  });

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 200,
      pinned: 'left',
    };
  }, []);

  const onSelectionChanged = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    console.log('Selected Row Details:', selectedRows);
    seteditRecord(selectedRows);
    // You can do further processing with selectedRows here, e.g., updating state
  };

  // CSV Upload Handler
  const handleCsvFileUpload = async (file) => {
    setCsvUploadLoading(true);
    try {
      const result = await parseCsvUpdateFile(file);

      if (!result.success) {
        notification.error({
          message: 'File Parsing Failed',
          description: result.errors.join(', '),
          duration: 5,
        });
        setCsvUploadLoading(false);
        return false;
      }

      // Show warnings if any
      if (result.errors.length > 0) {
        result.errors.forEach((error) => {
          if (error.startsWith('Warning:')) {
            notification.warning({
              message: error,
              duration: 3,
            });
          }
        });
      }

      // Match CSV data with existing rowData
      const previewData = result.data.map((csvRow) => {
        const existingRecord = rowData.find((row) => String(row.ID) === String(csvRow.ID));

        if (existingRecord) {
          return {
            ID: csvRow.ID,
            found: true,
            current: {
              JointReference: existingRecord.JointReference || '',
              WallThickness: existingRecord.WallThickness || '',
              PipeDiameter: existingRecord.PipeDiameter || '',
            },
            new: {
              JointReference: csvRow.JointReference || '',
              WallThickness: csvRow.WallThickness || '',
              PipeDiameter: csvRow.PipeDiameter || '',
            },
            existingRecord: existingRecord, // Store full record for update
            csvData: csvRow, // Store CSV data
          };
        } else {
          return {
            ID: csvRow.ID,
            found: false,
            current: {
              JointReference: '-',
              WallThickness: '-',
              PipeDiameter: '-',
            },
            new: {
              JointReference: csvRow.JointReference || '',
              WallThickness: csvRow.WallThickness || '',
              PipeDiameter: csvRow.PipeDiameter || '',
            },
            csvData: csvRow,
          };
        }
      });

      setCsvPreviewData(previewData);
      setCsvPreviewVisible(true);
      setCsvUploadLoading(false);
    } catch (error) {
      console.error('Error processing CSV file:', error);
      notification.error({
        message: 'Error Processing File',
        description: error.message || 'An error occurred while processing the file',
      });
      setCsvUploadLoading(false);
    }
    return false; // Prevent default upload behavior
  };

  // Bulk Update Handler with Batch Processing
  const handleBulkUpdate = async () => {
    // Filter only records that were found
    const recordsToUpdate = csvPreviewData.filter((item) => item.found);

    if (recordsToUpdate.length === 0) {
      notification.warning({
        message: 'No Records to Update',
        description: 'No matching records found to update',
      });
      return;
    }

    setCsvUpdateLoading(true);
    setUpdateProgress({ current: 0, total: recordsToUpdate.length });

    const BATCH_SIZE = 50;
    const batches = [];
    let successCount = 0;
    let failureCount = 0;
    const failedRecords = [];

    // Split into batches
    for (let i = 0; i < recordsToUpdate.length; i += BATCH_SIZE) {
      batches.push(recordsToUpdate.slice(i, i + BATCH_SIZE));
    }

    try {
      // Process batches sequentially
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        // Prepare update data for this batch
        const updateData = batch.map((item) => {
          const updatedRecord = { ...item.existingRecord };

          // Only update fields that have values in CSV (don't overwrite with empty strings unless CSV explicitly has empty value)
          if (item.csvData.JointReference !== undefined && item.csvData.JointReference !== null) {
            updatedRecord.JointReference = item.csvData.JointReference;
          }
          if (item.csvData.WallThickness !== undefined && item.csvData.WallThickness !== null) {
            updatedRecord.WallThickness = item.csvData.WallThickness;
          }
          if (item.csvData.PipeDiameter !== undefined && item.csvData.PipeDiameter !== null) {
            updatedRecord.PipeDiameter = item.csvData.PipeDiameter;
          }

          return updatedRecord;
        });

        try {
          // Call update API
          await onUpdateReportt({ data: updateData });

          successCount += batch.length;
          setUpdateProgress({ current: successCount, total: recordsToUpdate.length });

          // Small delay between batches to avoid overwhelming the server
          if (batchIndex < batches.length - 1) {
            await new Promise((resolve) => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`Error updating batch ${batchIndex + 1}:`, error);
          failureCount += batch.length;
          failedRecords.push(...batch.map((item) => item.ID));
        }
      }

      // Refresh table data
      const refreshObj = {
        pageSize: 100000,
        pageNumber: 1,
      };
      await onProjectList(refreshObj);

      // Show final notification
      if (failureCount === 0) {
        notification.success({
          message: 'Update Completed Successfully',
          description: `Successfully updated ${successCount} record(s)`,
          duration: 5,
        });
      } else {
        notification.warning({
          message: 'Update Completed with Errors',
          description: `Successfully updated ${successCount} record(s). ${failureCount} record(s) failed. Failed IDs: ${failedRecords.join(', ')}`,
          duration: 8,
        });
      }

      // Close preview modal
      setCsvPreviewVisible(false);
      setCsvPreviewData([]);
      setUpdateProgress({ current: 0, total: 0 });
    } catch (error) {
      console.error('Error in bulk update:', error);
      notification.error({
        message: 'Update Failed',
        description: error.message || 'An error occurred during the update process',
        duration: 5,
      });
    } finally {
      setCsvUpdateLoading(false);
    }
  };

  const modernPaginationTotal = modernWideTableActive
    ? modernColumnFilteredList.length
    : listCount != null
      ? listCount
      : 0;

  const tablePaginationModern =
    modernPaginationTotal > 0
      ? {
        position: ['bottomRight'],
        className: 'heat-profile-pagination',
        showSizeChanger: true,
        total: modernPaginationTotal,
        current: modernPageNumber,
        pageSize: modernPageSize,
        pageSizeOptions: ['10', '20', '50'],
        onChange: (pageNumber, pageSize) => {
          setModernPageNumber(pageNumber);
          setModernPageSize(pageSize || 10);
        },
      }
      : false;

  if (embeddedInModernLayout) {
    return (
      <>
        <div className="heat-profile-card">
          <div className="heat-profile-list-header">
            <div className="heat-profile-header-left">
              <div className="heat-profile-icon-wrap">
                <FileText className="lucide" size={22} strokeWidth={2} aria-hidden />
              </div>
              <div className="heat-profile-header-content">
                <div className="heat-profile-title-row">
                  <h3 className="heat-profile-list-title">List of Test Repository</h3>
                  <span className="heat-profile-summary">
                    {modernPaginationTotal} item{modernPaginationTotal !== 1 ? 's' : ''}
                  </span>
                </div>
                <p className="heat-profile-list-description">
                  Browse and filter coating technical test reports. View results and reports.
                </p>
              </div>
            </div>
            <div className="heat-profile-header-actions">
              <Upload
                accept=".csv,.xlsx,.xls"
                showUploadList={false}
                beforeUpload={handleCsvFileUpload}
                disabled={csvUploadLoading || csvUpdateLoading}
              >
                <CrcButton
                  label={csvUploadLoading ? 'Loading...' : 'Import CSV/Excel'}
                  variant="primary"
                  icon={UploadOutlined}
                  size="small"
                  onClick={() => { }}
                />
              </Upload>
            </div>
          </div>
          <div className="addTeamDetails heat-profile-table-wrap">
            <Table
              columns={modernColumnsWithFilters}
              dataSource={modernTableDataSource}
              rowKey="ID"
              loading={{
                indicator: <Spinner variant="inline" size="medium" />,
                spinning: isLoading,
              }}
              className="h-100 pipeline-table equipPaginationTable heat-profile-table"
              scroll={{ x: 2000 }}
              onChange={(pagination, filters, sorter, extra) => {
                // Only react to column-header sort; pagination also fires onChange but keeps the
                // previous sorter object — resetting page here broke pagination (always jumped to 1).
                if (extra?.action !== 'sort') return;
                const single = Array.isArray(sorter) ? sorter[0] : sorter;
                if (single?.columnKey) {
                  setSortField(single.columnKey);
                  setSortOrder(single.order || null);
                  setModernPageNumber(1);
                }
              }}
              pagination={tablePaginationModern}
            />
          </div>
        </div>
        {showAddNewModal && (
          <UpdateReport
            showAddNewModal={showAddNewModal}
            setShowAddNewModal={setShowAddNewModal}
            addNewForm={addNewForm}
            isEdit={isEdit}
            setIsEdit={setIsEdit}
            seteditRecord={seteditRecord}
            editRecord={editRecord}
            useModernLayout={embeddedInModernLayout}
          />
        )}
        {showTestPage && (
          <TechnicalReportPage
            showTestPage={showTestPage}
            setshowTestPage={setshowTestPage}
            setSelectedRow={setSelectedRow}
            selectedRow={selectedRow}
          />
        )}
        <Modal
          className="pipeline-modal"
          title="Results"
          centered
          visible={resultModalVisible}
          footer={[
            <Button key="apply" type="primary" onClick={() => setResultModalVisible(false)}>
              Ok
            </Button>,
          ]}
        >
          <div>{resultModalVisible && selectedRow && renderKeyValuePairs(selectedRow.Results)}</div>
        </Modal>

        {/* CSV Update Preview Modal */}
        <CsvUpdatePreviewModal
          visible={csvPreviewVisible}
          onCancel={() => {
            setCsvPreviewVisible(false);
            setCsvPreviewData([]);
          }}
          onConfirm={handleBulkUpdate}
          previewData={csvPreviewData}
          isLoading={csvUpdateLoading}
        />

        {/* Progress Modal for Batch Updates */}
        {csvUpdateLoading && updateProgress.total > 0 && (
          <Modal
            title="Updating Records"
            visible={csvUpdateLoading}
            footer={null}
            closable={false}
            maskClosable={false}
          >
            <div style={{ padding: '20px 0' }}>
              <Progress
                percent={Math.round((updateProgress.current / updateProgress.total) * 100)}
                status="active"
                format={(percent) => `${updateProgress.current} / ${updateProgress.total} records`}
              />
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Text>Updating records in batches. Please wait...</Text>
              </div>
            </div>
          </Modal>
        )}
      </>
    );
  }

  return (
    <div className="flex-auto">
      {!showTestPage && (
        <div className="flex-auto">
          <div className="d-flex mb-3 align-items-center tab-content-title">
            <div className="back_heatp_modal">
              <Tooltip title={'Back'}>
                <ArrowLeftOutlined
                  onClick={() => {
                    history.push('/technical');
                  }}
                />
              </Tooltip>
            </div>

            <Title
              className="mb-0 add-new-calibration"
              level={3}
              style={{ paddingLeft: '25px', paddingTop: '5px' }}
            >
              Coating Test Repository
            </Title>

            <div className="new-calibration">
              <Text
                className="ps-3 add-new-calibration"
                style={{ marginTop: '10px', marginRight: '20px' }}
              >
                {`Filters Count: ${listCount} / ${listTotalCount}`}
              </Text>
            </div>
          </div>
          <div className="custom-line" />

          <Row gutter={24}>
            <Col span={24}>
              <div className="addTeamDetails">
                <div
                  style={{ height: 'calc(112vh - 200px)', overflow: 'auto', position: 'relative' }}
                >
                  <div
                    className={`ag-grid ${theme} custom-grid-theme`}
                    style={gridStyle}
                  >
                    <AgGridReact
                      ref={gridRef}
                      rowData={rowData || []}
                      columnDefs={columnDefs}
                      rowDragManaged={true}
                      onRowDragEnd={onRowDragEnd}
                      defaultColDef={defaultColDef}
                      rowSelection={rowSelection}
                      onGridReady={onGridReady}
                      sideBar={true}
                      //
                      pivotPanelShow={'always'}
                      autoGroupColumnDef={autoGroupColumnDef}
                      animateRows={true}
                      onSelectionChanged={onSelectionChanged}
                      // enableFilter={true}
                      statusBar={statusBar}
                      rowHeight={40}
                      headerHeight={50}
                      defaultExcelExportParams={defaultExcelExportParams}
                      defaultCsvExportParams={defaultCsvExportParams}
                      loadingOverlayComponent={CustomLoadingCellRenderer}
                      loadingOverlayComponentParams={{
                        loadingMessage: 'Loading all rows...',
                      }}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      )}
      {showAddNewModal && (
        <UpdateReport
          showAddNewModal={showAddNewModal}
          setShowAddNewModal={setShowAddNewModal}
          addNewForm={addNewForm}
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          seteditRecord={seteditRecord}
          editRecord={editRecord}
          useModernLayout={embeddedInModernLayout}
        ></UpdateReport>
      )}
      {showTestPage && (
        <TechnicalReportPage
          showTestPage={showTestPage}
          setshowTestPage={setshowTestPage}
          setSelectedRow={setSelectedRow}
          selectedRow={selectedRow}
        ></TechnicalReportPage>
      )}

      <Modal
        className="pipeline-modal"
        title="Results"
        centered
        visible={resultModalVisible}
        footer={[
          <Button
            key="apply"
            type="primary"
            onClick={() => setResultModalVisible(false)}
          >
            {'Ok'}
          </Button>,
        ]}
      >
        <div>{resultModalVisible && renderKeyValuePairs(selectedRow.Results)}</div>
      </Modal>

      {/* CSV Update Preview Modal */}
      <CsvUpdatePreviewModal
        visible={csvPreviewVisible}
        onCancel={() => {
          setCsvPreviewVisible(false);
          setCsvPreviewData([]);
        }}
        onConfirm={handleBulkUpdate}
        previewData={csvPreviewData}
        isLoading={csvUpdateLoading}
      />

      {/* Progress Modal for Batch Updates */}
      {csvUpdateLoading && updateProgress.total > 0 && (
        <Modal
          title="Updating Records"
          visible={csvUpdateLoading}
          footer={null}
          closable={false}
          maskClosable={false}
        >
          <div style={{ padding: '20px 0' }}>
            <Progress
              percent={Math.round((updateProgress.current / updateProgress.total) * 100)}
              status="active"
              format={(percent) => `${updateProgress.current} / ${updateProgress.total} records`}
            />
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Text>Updating records in batches. Please wait...</Text>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
const mapStateToProps = ({ CrceTechnicalReducer }) => {
  const projectList = get(CrceTechnicalReducer, 'ProjectList', []);
  const isLoading = get(CrceTechnicalReducer, 'isProjectListLoading', false);

  const listCount = get(CrceTechnicalReducer, 'ProjectListCount', null);
  const listTotalCount = get(CrceTechnicalReducer, 'ProjectListTotalCount', null);

  return {
    projectList,
    isLoading,
    listCount,
    listTotalCount,
  };
};

const mapDispatchToProps = {
  onProjectList: fetchProjectList,
  onUpdateReportt: updateProjectTestList,
};

export default connect(mapStateToProps, mapDispatchToProps)(TechnicalPageAgGrid);
